// Skater stats (THPS): ten ratings from 1 to 10 that scale the sim's tuning,
// raised by spending stat points found in the parks or earned from goals.
// Sim-free and three.js-free: the Skater reads `statScale`, Game keeps the
// session's `StatSheet`, React draws the Stats screen.
//
// The default skater is an all-rounder with every stat at STAT_BASE, which
// is exactly the tuning in `sim/tuning.ts`: every scale is 1 there.

export const STAT_IDS = ["air", "hang", "ollie", "speed", "spin", "landing", "switch", "rail", "lip", "manual"] as const;
export type StatId = (typeof STAT_IDS)[number];
export type Stats = Record<StatId, number>;

export const STAT_MIN = 1;
export const STAT_MAX = 10;
/** Every stat of the default skater; scales are exactly 1 here. */
export const STAT_BASE = 5;

export interface StatInfo {
  label: string;
  /** What it does, for the Stats screen. */
  detail: string;
  /** Change in the scale per point above (or below) STAT_BASE. */
  perPoint: number;
}

/**
 * What each stat scales, and by how much. At 10 a stat is worth a noticeable
 * but not game-breaking edge (roughly +10-20%); at 1 a matching handicap.
 */
export const STAT_INFO: Record<StatId, StatInfo> = {
  air: { label: "Air", detail: "Height off vert lips", perPoint: 0.03 },
  hang: { label: "Hang time", detail: "Lighter in the air: more time for tricks", perPoint: -0.02 },
  ollie: { label: "Ollie", detail: "Ollie height", perPoint: 0.03 },
  speed: { label: "Speed", detail: "Cruise, crouch and top speed", perPoint: 0.02 },
  spin: { label: "Spin", detail: "How fast you spin in the air", perPoint: 0.04 },
  landing: { label: "Landing", detail: "How far off straight you can land, and how easily Perfect", perPoint: 0.05 },
  switch: { label: "Switch", detail: "Less of a penalty riding switch", perPoint: -0.18 },
  rail: { label: "Rail balance", detail: "Steadier grinds", perPoint: -0.06 },
  lip: { label: "Lip balance", detail: "Steadier lip tricks", perPoint: -0.06 },
  manual: { label: "Manual balance", detail: "Steadier manuals", perPoint: -0.06 },
};

export const DEFAULT_STATS: Readonly<Stats> = Object.freeze(
  Object.fromEntries(STAT_IDS.map((id) => [id, STAT_BASE])) as Stats,
);

/**
 * The multiplier a stat puts on what it scales: exactly 1 at STAT_BASE.
 * `air`, `ollie`, `speed`, `spin`, `landing` multiply what they name; `hang`
 * multiplies air gravity; `switch` is the power the switch penalties are
 * raised to; the balance stats multiply their needle's wobble.
 */
export function statScale(stats: Readonly<Stats>, id: StatId): number {
  return 1 + STAT_INFO[id].perPoint * (stats[id] - STAT_BASE);
}

/** The session's stats and points to spend, for the Stats screen (#35). */
export interface StatsHud {
  /** The skater being ridden's stats. */
  stats: Readonly<Stats>;
  /** Their starting spread: the pips drawn in ink, and the floor for taking points back. */
  base: Readonly<Stats>;
  points: number;
}

/**
 * The session's stats and unspent stat points (session-only until saving
 * exists). Points are earned into one pool and spent per skater (#64): each
 * skater keeps their own sheet, starting from their own spread. A point
 * spent this session can be taken back, but no stat goes below the
 * skater's starting spread.
 */
export class StatSheet {
  /** The skater being ridden's sheet; raising a stat changes it in place, so a `Skater` holding it sees the change. */
  stats: Stats;
  /** Unspent points, shared by every skater. */
  points = 0;
  /** Every point earned this session, spent or not. */
  earned = 0;
  /** Bumped on every change, so the HUD knows to redraw. */
  version = 0;
  private base: Readonly<Stats>;
  private sheets = new Map<string, Stats>();
  private hudState: StatsHud | null = null;
  private hudVersion = -1;

  /** Starts on `base` (the default skater's all-5s unless given), filed under `id`. */
  constructor(base: Readonly<Stats> = DEFAULT_STATS, id = "default") {
    this.base = base;
    this.stats = { ...base };
    this.sheets.set(id, this.stats);
  }

  /** Switches to skater `id`'s sheet, starting it from `base` the first time. */
  select(id: string, base: Readonly<Stats>): void {
    let sheet = this.sheets.get(id);
    if (!sheet) this.sheets.set(id, (sheet = { ...base }));
    this.stats = sheet;
    this.base = base;
    this.version++;
  }

  /** The Stats screen's state, rebuilt only when the sheet changes. */
  hud(): StatsHud {
    if (this.hudState && this.hudVersion === this.version) return this.hudState;
    this.hudVersion = this.version;
    return (this.hudState = { stats: { ...this.stats }, base: this.base, points: this.points });
  }

  award(n = 1): void {
    this.points += n;
    this.earned += n;
    this.version++;
  }

  /** Spends a point on `id`; false with none to spend or the stat maxed. */
  raise(id: StatId): boolean {
    if (this.points <= 0 || this.stats[id] >= STAT_MAX) return false;
    this.stats[id]++;
    this.points--;
    this.version++;
    return true;
  }

  /** Takes a point back off `id`; false at the skater's starting rating. */
  lower(id: StatId): boolean {
    if (this.stats[id] <= this.base[id]) return false;
    this.stats[id]--;
    this.points++;
    this.version++;
    return true;
  }
}
